import type { ServiceInput, ServicePayload } from '@seek/indie-api-types';
import {
  keepPreviousData,
  useQuery,
  type UseQueryResult,
} from '@tanstack/react-query';
import {
  Alert,
  Box,
  ButtonIcon,
  Column,
  Columns,
  Drawer,
  Heading,
  IconShare,
  Loader,
  Pagination,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  TabPanel,
  TabPanels,
  Tabs,
  TabsProvider,
  Text,
  TextLinkButton,
  useToast,
} from 'braid-design-system';
import { format } from 'date-fns/format';
import { parse, print } from 'graphql';
import { useEffect, useId, useMemo, useState } from 'react';
import { CodeContainer, InlineCode } from 'scoobie';
import { useApi } from '#src/api/index.ts';
import { LoadingPlaceholder } from '#src/components/LoadingPlaceholder/LoadingPlaceholder.tsx';
import { joinWithAnd } from '../utilities.ts';
import { OperationTypeNames, StatusNames, UsageTypeNames } from './config.ts';
import { shouldConsiderAsLoading } from './loading.ts';
import type { FilterData } from './types.ts';
import { useLocation } from 'react-router';
import { StringParam, useQueryParam } from 'use-query-params';

const formatDate = (date: Date) => format(date, 'MMM d, HH:mm:ss');

type Request = ServicePayload.IntegrationRequests['requests'][0];

const RequestBodyDisplay = ({ requestBody }: { requestBody: string }) => {
  const isValidJson = useMemo(() => {
    try {
      JSON.parse(requestBody);
      return true;
    } catch {
      return false;
    }
  }, [requestBody]);

  return (
    <Stack space="small">
      {!isValidJson && requestBody ? (
        <Alert tone="caution">
          <Text>Request body is invalid JSON</Text>
        </Alert>
      ) : null}

      {requestBody === '' ? (
        <Text tone="secondary">No request body</Text>
      ) : (
        <CodeContainer
          language="JSON"
          lineNumbers
          code={
            isValidJson
              ? JSON.stringify(JSON.parse(requestBody), null, 2)
              : requestBody
          }
        />
      )}
    </Stack>
  );
};

type Column = {
  label: string;
  render: (request: Request) => React.ReactNode;
};

const statusToTone = (status: Request['status']) => {
  switch (status) {
    case 'OK':
      return 'positive';
    case 'ClientError':
      return 'caution';
    case 'ServerError':
      return 'critical';
  }
};

const placeholderRequest: Request = {
  eventId: '00000000-0000-0000-0000-000000000000',
  externalRequestId: '00000000-0000-0000-0000-000000000000',
  requestTime: '2021-01-01T00:00:00Z',
  requestLatency: 0,
  statusCode: 200,
  status: 'OK',
  requestBody: '{}',
  requestType: 'GraphQL',
  acceptLanguageHeader: '',
  graphQLOperations: [
    {
      errors: [],
      latency: 0,
      operationType: 'query',
      opFieldResults: [],
      operationName: '',
    },
  ],
  url: '',
  userAgent: '',
  ipAddress: '',
  dataType: 'live',
  usageType: 'browser',
};

const timeColumn: Column = {
  label: 'Time',
  render: ({ requestTime }) => (
    <Text>
      <span title={requestTime}>{formatDate(new Date(requestTime))}</span>
    </Text>
  ),
};

const typeColumn: Column = {
  label: 'Type',
  render: ({ requestType }) => <Text>{requestType}</Text>,
};

const requestIdColumn: Column = {
  label: 'Request ID',
  render: ({ externalRequestId }) => (
    <span>
      <InlineCode>{externalRequestId}</InlineCode>
    </span>
  ),
};

const detailsColumns: Column[] = [
  timeColumn,
  {
    label: 'Time (UTC)',
    render: ({ requestTime }) => <Text>{requestTime}</Text>,
  },
  typeColumn,
  requestIdColumn,
  {
    label: 'Status',
    render: ({ status }) => <Text>{StatusNames[status]}</Text>,
  },
  {
    label: 'Token type',
    render: ({ usageType }) => <Text>{UsageTypeNames[usageType]}</Text>,
  },
  {
    label: 'GraphQL operation type',
    render: (request) => {
      const types = Array.from(
        new Set(
          request.graphQLOperations?.map(
            (op) => OperationTypeNames[op.operationType],
          ) ?? [],
        ),
      );

      return <Text>{types.length ? joinWithAnd(types) : 'N/A'}</Text>;
    },
  },
  {
    label: 'HTTP Status Code',
    render: ({ statusCode }) => <Text>{statusCode}</Text>,
  },
  { label: 'IP Address', render: ({ ipAddress }) => <Text>{ipAddress}</Text> },
  { label: 'URL', render: ({ url }) => <Text>{url}</Text> },
  { label: 'User agent', render: ({ userAgent }) => <Text>{userAgent}</Text> },
  {
    label: 'Accept-Language',
    render: ({ acceptLanguageHeader }) => <Text>{acceptLanguageHeader}</Text>,
  },
  {
    label: 'Hirer',
    render: ({ hirerOid, usageType }) => (
      <Text>
        {usageType === 'browser' ? hirerOid : `None (${usageType} token)`}
      </Text>
    ),
  },
];

const GraphQLOperations = ({
  graphQLOperations,
  requestBody,
}: {
  graphQLOperations: Exclude<Request['graphQLOperations'], undefined>;
  requestBody: string;
}) => {
  const location = useLocation();
  const [operationIndex, setOperationIndex] = useState(0);
  const operation = graphQLOperations[operationIndex];

  const maybeOperation = useMemo(() => {
    try {
      const parsed = JSON.parse(requestBody);
      const operations = Array.isArray(parsed) ? parsed : [parsed];
      let formattedQuery = operations[operationIndex].query;

      try {
        formattedQuery = print(parse(formattedQuery));
      } catch {}

      return {
        query: formattedQuery,
        variables: JSON.stringify(
          operations[operationIndex].variables ?? {},
          null,
          2,
        ),
      };
    } catch {
      return null;
    }
  }, [requestBody, operationIndex]);

  return (
    <Stack space="large">
      {graphQLOperations.length > 1 ? (
        <Pagination
          page={operationIndex + 1}
          total={graphQLOperations.length}
          label="Operation number"
          linkProps={({ page }) => ({
            href: location.search,
            onClick: (e) => {
              e.preventDefault();
              setOperationIndex(page - 1);
            },
          })}
        />
      ) : null}

      <Table label="Operation">
        <TableBody>
          <TableRow>
            <TableHeaderCell width="content">
              <Text>Operation type</Text>
            </TableHeaderCell>
            <TableCell wrap>
              <Text>{OperationTypeNames[operation.operationType]}</Text>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableHeaderCell width="content">
              <Text>Operation name</Text>
            </TableHeaderCell>
            <TableCell wrap>
              {operation.operationName ? (
                <Text>{operation.operationName}</Text>
              ) : (
                <Text tone="secondary">None</Text>
              )}
            </TableCell>
          </TableRow>
          <TableRow>
            <TableHeaderCell width="content">
              <Text>Latency</Text>
            </TableHeaderCell>
            <TableCell wrap>
              <Text>{operation.latency.toFixed(2)}ms</Text>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableHeaderCell width="content">
              <Text>Operation</Text>
            </TableHeaderCell>
            <TableCell wrap>
              {maybeOperation ? (
                <CodeContainer language="GraphQL" code={maybeOperation.query} />
              ) : (
                <Text tone="critical">Could not parse operation</Text>
              )}
            </TableCell>
          </TableRow>
          <TableRow>
            <TableHeaderCell width="content">
              <Text>Variables</Text>
            </TableHeaderCell>
            <TableCell wrap>
              {maybeOperation ? (
                <CodeContainer
                  language="JSON"
                  code={maybeOperation.variables}
                />
              ) : (
                <Text tone="critical">Could not parse operation</Text>
              )}
            </TableCell>
          </TableRow>
          <TableRow>
            <TableHeaderCell width="content">
              <Text>Errors</Text>
            </TableHeaderCell>
            <TableCell wrap>
              {operation.errors.length ? (
                <CodeContainer
                  language="JSON"
                  code={JSON.stringify(operation.errors, null, 2)}
                />
              ) : (
                <Text>No errors</Text>
              )}
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </Stack>
  );
};

export const RequestDetailsTable = ({
  bounds,
  filters,
}: {
  bounds: [Date, Date];
  filters: FilterData[];
}) => {
  const showToast = useToast();
  const [drawerEventId, setDrawerEventId] = useQueryParam('id', StringParam, {
    removeDefaultsFromUrl: true,
  });
  const [currentPage, setCurrentPage] = useState(1);
  useEffect(() => {
    setCurrentPage(1);
  }, [filters, bounds]);

  const columns = useMemo<Column[]>(
    () => [
      timeColumn,
      typeColumn,
      requestIdColumn,
      {
        label: 'Result',
        render: ({ status, statusCode }) => (
          <Text tone={statusToTone(status)}>
            {StatusNames[status]}
            {statusCode !== 200 ? ` (HTTP Status: ${statusCode})` : ''}
          </Text>
        ),
      },
      {
        label: 'Request details',
        render: (request: Request) => (
          <Text>
            <TextLinkButton onClick={() => setDrawerEventId(request.eventId)}>
              View
            </TextLinkButton>
          </Text>
        ),
      },
    ],
    [setDrawerEventId],
  );

  const api = useApi();

  const [boundsStart, boundsEnd] = bounds;
  const requests = useQuery({
    queryKey: [
      'logs',
      boundsStart.toISOString(),
      boundsEnd.toISOString(),
      ...filters.map((f) => [f.id]),
      currentPage,
      api,
    ],
    queryFn: async ({ signal }) => {
      const parameters: ServiceInput.IntegrationRequests = {
        timeRange: {
          start: boundsStart.toISOString(),
          end: boundsEnd.toISOString(),
        },
        // @ts-expect-error - annoying types
        filters: filters.map((f) => ({
          type: f.type,
          comparator: f.comparator,
          value: f.value,
        })),
        pageNumber: currentPage,
      };
      return {
        parameters,
        results: await api.integrationRequests.get(parameters, signal),
      };
    },
    placeholderData: keepPreviousData,
  });

  const fetchedRequestForDrawer = useQuery({
    queryKey: ['request', drawerEventId ?? 'none', requests, api],
    queryFn: async ({ signal }) => {
      if (!drawerEventId) {
        return null;
      }

      const fromRequest = requests.data?.results.requests.find(
        (request) => request.eventId === drawerEventId,
      );
      if (fromRequest) {
        return fromRequest;
      }

      const result = await api.integrationRequests.get(
        {
          timeRange: {
            start: new Date(0).toISOString(),
            end: new Date().toISOString(),
          },
          filters: [
            {
              type: 'EventId',
              comparator: 'Equal',
              value: drawerEventId,
            },
          ],
        },
        signal,
      );

      return result.requests[0];
    },
  });

  const isLoading = useMemo(
    () =>
      requests.fetchStatus === 'fetching' &&
      shouldConsiderAsLoading({
        query: { data: requests.data, status: requests.status },
        filters,
        bounds,
      }),
    [requests.data, requests.fetchStatus, requests.status, filters, bounds],
  );

  const requestDetailsDrawerId = useId();

  const drawerRequest = fetchedRequestForDrawer.data;

  const modal = drawerEventId ? (
    <Drawer
      open
      onClose={() => setDrawerEventId(null)}
      title="Request details"
      id={requestDetailsDrawerId}
      width="large"
    >
      <Stack space="small">
        {fetchedRequestForDrawer.status === 'pending' ? <Loader /> : null}

        {fetchedRequestForDrawer.status === 'error' ? (
          <Alert tone="critical">
            <Text>Failed to load request details</Text>
          </Alert>
        ) : null}

        {fetchedRequestForDrawer.status === 'success' ? (
          <>
            {drawerRequest ? (
              <TabsProvider id={`${requestDetailsDrawerId}-tabs`}>
                <Columns space="medium">
                  <Column width="content">
                    <Tabs label="Request details" divider="full">
                      <Tab>Details</Tab>
                      <Tab>Body</Tab>
                      {drawerRequest.graphQLOperations?.length ? (
                        <Tab>GraphQL operations</Tab>
                      ) : null}
                    </Tabs>
                  </Column>
                  <Column>
                    <ButtonIcon
                      id={`${requestDetailsDrawerId}-share`}
                      label="Share a link to this request"
                      icon={<IconShare />}
                      bleed
                      size="small"
                      onClick={async () => {
                        await navigator.clipboard.writeText(
                          window.location.href,
                        );

                        showToast({
                          message: 'Sharing link copied to clipboard',
                          tone: 'positive',
                        });
                      }}
                    />
                  </Column>
                </Columns>
                <TabPanels>
                  <TabPanel>
                    <Table label="Request details">
                      <TableBody>
                        {detailsColumns.map((column) => (
                          <TableRow key={column.label}>
                            <TableHeaderCell width="content">
                              <Text>{column.label}</Text>
                            </TableHeaderCell>
                            <TableCell wrap>
                              <Box>{column.render(drawerRequest)}</Box>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TabPanel>
                  <TabPanel>
                    <RequestBodyDisplay
                      requestBody={drawerRequest.requestBody}
                    />
                  </TabPanel>
                  {drawerRequest.graphQLOperations?.length ? (
                    <TabPanel>
                      <GraphQLOperations
                        graphQLOperations={drawerRequest.graphQLOperations}
                        requestBody={drawerRequest.requestBody}
                      />
                    </TabPanel>
                  ) : null}
                </TabPanels>
              </TabsProvider>
            ) : (
              <Alert tone="caution">
                <Text>Request not found</Text>
              </Alert>
            )}
          </>
        ) : null}
      </Stack>
    </Drawer>
  ) : null;

  return (
    <>
      {modal}
      <TableView
        requests={requests}
        isLoading={isLoading}
        columns={columns}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
      />
    </>
  );
};

const TableView = ({
  requests,
  columns,
  isLoading,
  currentPage,
  setCurrentPage,
}: {
  requests: UseQueryResult<{
    parameters: ServiceInput.IntegrationRequests;
    results: ServicePayload.IntegrationRequests;
  }>;
  columns: Column[];
  isLoading: boolean;
  currentPage: number;
  setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
}) => {
  const location = useLocation();

  if (requests.status === 'error') {
    return (
      <Alert tone="critical">
        <Text>Failed to load request details</Text>
      </Alert>
    );
  }

  if (requests.status === 'pending' || isLoading) {
    return (
      <Stack space="small">
        <Heading level="4">Request details</Heading>
        <LoadingPlaceholder>
          <Text tone="secondary">Showing recent matching requests</Text>
        </LoadingPlaceholder>
        <Table label="Request details">
          <TableHeader>
            <TableRow>
              {columns.map((column) => (
                <TableHeaderCell key={column.label}>
                  <Text>{column.label}</Text>
                </TableHeaderCell>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {Array.from({ length: 20 }).map((_, i) => (
              <TableRow key={i}>
                {columns.map((column) => (
                  <TableCell key={column.label} wrap>
                    <LoadingPlaceholder key={column.label}>
                      {column.render(placeholderRequest)}
                    </LoadingPlaceholder>
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Stack>
    );
  }

  const PAGE_LENGTH = 20;
  const totalRequests = requests.data.results.totalRequests;

  let description;
  if (totalRequests === 0) {
    description = 'No requests matching the filters and time window found';
  } else if (totalRequests === PAGE_LENGTH) {
    const start = (currentPage - 1) * PAGE_LENGTH + 1;
    const end = currentPage * PAGE_LENGTH;
    description = `Showing ${start}-${end} of ${totalRequests.toLocaleString()} most recent requests matching the filters and time window`;
  } else {
    description = `Showing last ${(totalRequests ?? 0)?.toLocaleString()} request${totalRequests === 1 ? '' : 's'} matching the filters and time window`;
  }

  return (
    <Stack space="small">
      <Heading level="4">Request details</Heading>
      <Text tone="secondary">{description}</Text>

      {requests.data.results.requests.length === 0 ? null : (
        <Table label="Request details">
          <TableHeader>
            <TableRow>
              {columns.map((column) => (
                <TableHeaderCell key={column.label}>
                  <Text>{column.label}</Text>
                </TableHeaderCell>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {requests.data.results.requests.map((request) => (
              <TableRow key={request.eventId}>
                {columns.map((column) => (
                  <TableCell key={column.label} wrap>
                    {column.render(request)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      {typeof requests.data.results.totalPageCount === 'number' &&
      requests.data.results.totalPageCount > 1 ? (
        <Pagination
          page={currentPage}
          total={requests.data.results.totalPageCount}
          label="Pagination of requests"
          linkProps={({ page }) => ({
            href: location.search,
            onClick: (e) => {
              e.preventDefault();
              setCurrentPage(page);
            },
          })}
        />
      ) : null}
    </Stack>
  );
};
